import { StaffCapability, StaffMember, StaffShift } from '@/api'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'

type StaffListProps = {
  staff: StaffMember[]
  onEdit: (index: number) => void
  onDelete: (index: number) => void
  onMove: (index: number, direction: -1 | 1) => void
  disabled?: boolean
}

const shiftLabelMap: Record<StaffShift, string> = {
  [StaffShift.DAY]: '昼',
  [StaffShift.EVENING]: '夕',
  [StaffShift.NIGHT]: '夜',
  [StaffShift.OPERATOR]: 'オペレーター',
}

const capabilitySymbolMap: Record<StaffCapability, string> = {
  enabled: '◯',
  partial: '△',
  disabled: '✕',
}

const capabilitySymbol = (value: StaffCapability) => capabilitySymbolMap[value] ?? '✕'

const shifts: StaffShift[] = [StaffShift.DAY, StaffShift.EVENING, StaffShift.NIGHT, StaffShift.OPERATOR]

const StaffList = ({ staff, onEdit, onDelete, onMove, disabled }: StaffListProps) => {
  return (
    <Card title="スタッフ一覧">
      {staff.length === 0 ? (
        <p className="m-0 text-600">スタッフが登録されていません。</p>
      ) : (
        <div className="flex flex-column gap-4">
          {shifts.map((shift) => {
            const members = staff
              .map((member, index) => ({ member, index }))
              .filter((entry) => entry.member.shift === shift)
            if (members.length === 0) {
              return null
            }

            return (
              <div key={shift} className="border-1 surface-border border-round overflow-x-auto">
                <table className="border-collapse">
                  <tbody>
                    <tr>
                      <th className="text-left p-2 border-1 surface-border surface-100" style={{ width: '140px' }}>
                        {shiftLabelMap[shift]}勤
                      </th>
                      {members.map(({ member, index }) => (
                        <th key={`${member.name}-${index}`} className="text-center p-2 border-1 surface-border" style={{ width: '140px' }}>{member.name}</th>
                      ))}
                    </tr>
                    <tr>
                      <th className="text-left p-2 border-1 surface-border surface-50">便蓋品</th>
                      {members.map(({ member, index }) => (
                        <td key={`lidded-${index}`} className="p-2 border-1 surface-border text-center">{capabilitySymbol(member.can_handle_lidded)}</td>
                      ))}
                    </tr>
                    <tr>
                      <th className="text-left p-2 border-1 surface-border surface-50">外観品</th>
                      {members.map(({ member, index }) => (
                        <td key={`appearance-${index}`} className="p-2 border-1 surface-border text-center">{capabilitySymbol(member.can_handle_appearance)}</td>
                      ))}
                    </tr>
                    <tr>
                      <th className="text-left p-2 border-1 surface-border surface-50">その他</th>
                      {members.map(({ member, index }) => (
                        <td key={`other-${index}`} className="p-2 border-1 surface-border text-center">{capabilitySymbol(member.can_handle_other)}</td>
                      ))}
                    </tr>
                    <tr>
                      <th className="text-left p-2 border-1 surface-border surface-50">操作</th>
                      {members.map(({ index }, memberPosition) => (
                        <td key={`actions-${index}`} className="p-2 border-1 surface-border">
                          <div className="flex gap-2 justify-content-center flex-wrap">
                            <Button
                              label=""
                              icon="pi pi-arrow-left"
                              outlined
                              onClick={() => onMove(index, -1)}
                              disabled={disabled || memberPosition === 0}
                              size="small"
                            />
                            <Button
                              label=""
                              icon="pi pi-arrow-right"
                              outlined
                              onClick={() => onMove(index, 1)}
                              disabled={disabled || memberPosition === members.length - 1}
                              size="small"
                            />
                            <Button
                              label=""
                              icon="pi pi-pencil"
                              outlined
                              onClick={() => onEdit(index)}
                              disabled={disabled}
                              size="small"
                            />
                            <Button
                              label=""
                              icon="pi pi-trash"
                              severity="danger"
                              outlined
                              onClick={() => onDelete(index)}
                              disabled={disabled}
                              size="small"
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}

export default StaffList
